/** Pure navigation policy, shared by boot, login and the start-page selector. */
export type PageRef = { feature: string; page: string };
export type PageRoute = PageRef & {
  href: string;
  title: string;
  showInMenu?: boolean;
  requiredPermissions: string[];
};
export type NavigationUser = { permissions: string[]; start_page?: PageRef | null };

export const normalizePath = (path: string) => path.replace(/\/+$/, "") || "/";
export const pageKey = (page?: PageRef | null) => page ? `${page.feature}/${page.page}` : "";
export const canOpen = (route: PageRoute, permissions: string[]) =>
  !route.requiredPermissions.length || route.requiredPermissions.some(p => permissions.includes(p));

export function startPageOptions<T extends PageRoute>(routes: T[], permissions: string[]): T[] {
  return routes.filter(r => r.showInMenu !== false && canOpen(r, permissions))
    .sort((a, b) => a.feature.localeCompare(b.feature) || a.title.localeCompare(b.title));
}

export function homeRoute<T extends PageRoute>(
  routes: T[], configured: PageRef | null, user: NavigationUser,
): T | undefined {
  for (const candidate of [user.start_page, configured, { feature: "user-authentication", page: "account" }]) {
    const route = routes.find(r => pageKey(r) === pageKey(candidate));
    if (route && route.showInMenu !== false && canOpen(route, user.permissions)) return route;
  }
  // Never choose an arbitrary first feature, nor recurse through unavailable fallbacks.
  return undefined;
}

export function loginDestination<T extends PageRoute>(
  routes: T[], configured: PageRef | null, user: NavigationUser, safePath: string,
): { href: string; fallback: boolean } {
  const path = normalizePath(safePath.split(/[?#]/, 1)[0]);
  const route = routes.find(r => r.href === path);
  const authFlow = route?.feature === "user-authentication" && ["login", "elevate"].includes(route.page);
  if (route && !authFlow && canOpen(route, user.permissions)) return { href: safePath, fallback: false };
  return { href: homeRoute(routes, configured, user)?.href ?? "/", fallback: path !== "/" };
}
